<template>
  <div class="container-fluid">
    <div class="row">
      <div
        :class="{
          'col-lg-9': (actions.length || (models && Object.keys(models).length)) && currentTabCode === 'info',
          'col-xl-12': currentTabCode !== 'info',
        }"
      >
        <TrainingInfosComponent
          :training="training"
          :loading="infoLoading"
          :logo="logo"
          :currentTabCode="currentTabCode"
          :npecModels="npecModels"
          :cpneList="cpneList"
          :settingsModels="settingsModels"
          @toggleTab="toggleTab"
          @updateField="getCpneList"
          @refresh="getTraining(true)"
        />
        <TrainingBlockComponent
          v-if="currentTabCode === 'info'"
          :training="training"
          :registrationList="registrationList"
          :registrationColumns="registrationColumns"
          :registrationCount="registrationCount"
          :registrationLoading="registrationLoading"
          :groupLoading="groupLoading"
          :trainingLoading="infoLoading"
          :isFiltered="registrationListIsFiltered"
          :registrationLimit="registrationListParams?.limit ? registrationListParams.limit : 10"
          @createRegistration="createRegistration"
          @deleteGroup="deleteGroup"
          @refresh="refresh"
          @openSearch="openSearch"
          @resetSearch="resetSearch"
        />
      </div>
      <div
        v-if="(actions.length || (models && Object.keys(models).length)) && currentTabCode === 'info'"
        class="col-lg-5 col-xl-3"
      >
        <ActionsBlock v-if="actions && actions.length" :actions="actions" />
        <TrainingContactBlock
          v-if="models && Object.keys(models).length"
          :models="models"
          :loading="contactLoading"
          @submit="submit"
        />
      </div>
    </div>
    <ModalFormComponent
      :models="modalModels"
      :title="modalTitle"
      :storageKey="searchStorageKey"
      @submit="submitRegistrationCreateModal"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";
import TrainingInfosComponent from "@/components/training/TrainingInfosComponent.vue";
import { i18n } from "@/i18n";
import { trainingManager } from "@/services/training/training-manager";
import { pictureManager } from "@/services/utilities/picture-manager";
import { registrationManager } from "@/services/registration/registration-manager";
import { formManager } from "@/services/form/form-manager";
import { useStudeaManagerStore } from "@/store/studea_manager/studea-manager";
import { useMainStore } from "@/store/main/main";
import { useUserStore } from "@/store/user/user";
import ModalFormComponent from "@/components/form/ModalFormComponent.vue";
import { moduleManager } from "@/services/module/module-manager";
import { moduleConstantes } from "@/constantes/module";
import { usePlatformStore } from "@/store/platform/platform";
import { dataTableManager } from "@/services/data-table/data-table-manager";
import { notificationManager } from "@/services/utilities/notification-manager";
import TrainingBlockComponent from "@/components/training/TrainingBlockComponent.vue";
import { accessConstante } from "@/constantes/access";
import { accessManager } from "@/services/security/access-manager";
import TrainingContactBlock from "@/components/training/TrainingContactBlock.vue";
import ActionsBlock from "@/components/blocks/ActionsBlock.vue";

export default {
  name: "TrainingDashboardView",
  components: {
    TrainingContactBlock,
    TrainingBlockComponent,
    ModalFormComponent,
    TrainingInfosComponent,
    ActionsBlock,
  },
  data() {
    return {
      settingsModels: {},
      training: {},
      contactLoading: false,
      infoLoading: false,
      groupLoading: false,
      models: null,
      cpneList: {},
      logo: null,
      npecModels: {},
      oldNpecModels: {},
      currentTabCode: "info",
      modalModels: {},
      modalType: "",
      registrationListIsFiltered: false,
      searchStorageKey: "",
      registrationCount: 0,
      registrationList: {},
      registrationColumns: {},
      registrationListParams: {},
      registrationLoading: false,
      modalTitle: null,
      currentGroupCryptedId: null,
      submitModel: {
        vars: {
          hideCancel: true,
        },
      },
      actions: [],
    };
  },
  watch: {
    modulesAvailable() {
      this.initActions();
      this.getRegistrationList();
    },
  },
  computed: {
    ...mapState(useUserStore, {
      user: (store) => store.user,
      permission: (store) => store.permission,
    }),
    ...mapState(usePlatformStore, {
      modulesAvailable: (store) => store.modulesAvailable,
    }),
  },
  mounted() {
    this.closeSideBar();
    this.init();
    this.setAppLoading(false);
  },
  methods: {
    init() {
      if (sessionStorage.getItem("studea-training-registration-" + this.$route.params.trainingId)) {
        this.registrationListIsFiltered = true;
      }
      this.setParams();
      this.getCurrentTabCode();
      this.getTabData();
      this.getTraining();
      this.getContactForm();
      this.getRegistrationList();
      this.initActions();
    },

    setParams() {
      let search;
      if (sessionStorage.getItem("studea-training-registration-" + this.$route.params.trainingId)) {
        search = dataTableManager.getParamsFormSessionStorage(
          sessionStorage.getItem("studea-training-registration-" + this.$route.params.trainingId),
        );
      }
      this.registrationListParams = dataTableManager.getListParams(search);
    },

    getTabData() {
      const tabHandlers = {
        npec: () => this.getNpecForm(),
        settings: () => this.getRegistrationSettings(),
        rncp: () => {
          this.loading = false;
        },
      };

      tabHandlers[this.currentTabCode]?.();
    },

    getCurrentTabCode() {
      if (window.location.hash) {
        this.currentTabCode = window.location.hash.substring(1);
      }
    },

    async getTraining(forceRefresh) {
      if (!Object.keys(this.training).length || forceRefresh) {
        if (this.currentTabCode !== "rncp") {
          this.infoLoading = true;
          this.groupLoading = true;
        }
        this.setTrainingName("");

        try {
          const response = await trainingManager.getTraining(this.$route.params.trainingId);
          this.training = response;
          this.setTrainingName(response.displayName);
          this.setLogoSrc();
        } finally {
          this.infoLoading = false;
          this.groupLoading = false;
        }
      }
    },

    async getCpneList(payload) {
      if (payload?.importVersion) {
        this.infoLoading = true;

        try {
          this.cpneList = await trainingManager.getSimulatorList(this.$route.params.trainingId, payload);
        } finally {
          this.infoLoading = false;
        }
      }
    },

    async getRegistrationList() {
      if (moduleManager.isModuleActive(moduleConstantes.MODULE_STUDEA_REGISTRATION)) {
        this.groupLoading = true;
        const response = await trainingManager.getRegistrationList(
          this.$route.params.trainingId,
          this.registrationListParams,
        );
        if (response) {
          this.registrationList = response.data;
          this.registrationColumns = dataTableManager.getSortTableColumns(response.columns, this.registrationColumns, [
            "mission",
          ]);
          this.registrationCount = response.count;
          this.groupLoading = false;
        }
      }
    },

    initActions() {
      if (this.user && this.user.roles.includes("ROLE_STUDEA_ADMIN")) {
        this.actions.push({
          actionType: "router",
          icon: "pen",
          type: "primary",
          action: "adminTrainingsEdit",
          translation: i18n.global.tc("global.edit.title", 0, {
            name: i18n.global.t("registration.training").toLowerCase(),
          }),
          cfaStyle: true,
          params: { id: this.$route.params.trainingId, alias: "training" },
        });
      }
      if (
        moduleManager.isModuleActive(moduleConstantes.MODULE_STUDEA_REGISTRATION) &&
        accessManager.isAvailableForUser(null, accessConstante.STUDEA_MANAGER_REGISTRATION, accessConstante.ALL)
      ) {
        this.actions.push({
          actionType: "router",
          icon: "eye",
          type: "primary",
          action: "RegistrationList",
          translation: i18n.global.t("training.contract_list"),
          cfaStyle: true,
        });
      }
    },

    async submit() {
      this.contactLoading = true;

      try {
        const result = formManager.processForm(this.models);
        await trainingManager.put(null, this.$route.params.trainingId, "contact", result);
        notificationManager.showNotification("success", i18n.global.t("training.contacts.success"));
      } finally {
        this.contactLoading = false;
      }
    },

    async getContactForm() {
      this.contactLoading = true;

      try {
        this.models = await trainingManager.form("contact", null, false, this.$route.params.trainingId);
      } finally {
        this.contactLoading = false;
      }
    },

    async getRegistrationSettings() {
      this.infoLoading = true;

      try {
        this.settingsModels = await trainingManager.form(
          "training/registration-settings",
          this.$route.params.trainingId,
        );
      } finally {
        this.infoLoading = false;
      }
    },

    async getNpecForm() {
      this.infoLoading = true;
      this.npecModels = [];
      this.cpneList = [];

      try {
        this.npecModels = await trainingManager.form("training/simulator", this.$route.params.trainingId);
        if (this.npecModels.importVersion && this.training.titleRncp && Object.keys(this.training.titleRncp).length) {
          this.npecModels.importVersion.vars.value = this.npecModels.importVersion.vars.choices[0];
          await this.getCpneList({ importVersion: this.npecModels?.importVersion?.vars?.value?.value });
        }
      } finally {
        this.infoLoading = false;
      }
    },

    toggleTab(code) {
      this.setCurrentTabCode(code);
      this.getTabData();
    },

    setCurrentTabCode(code) {
      this.currentTabCode = code;
      window.location.hash = this.currentTabCode;
    },

    setLogoSrc() {
      this.logo = pictureManager.setPicture(this.training.logoTraining);
    },

    resetModalContent() {
      this.modalModels = {};
      this.modalTitle = null;
    },

    async createRegistration(groupCryptedId) {
      this.resetModalContent();
      this.searchStorageKey = "";
      this.groupLoading = true;

      try {
        this.modalModels = await registrationManager.getRegistrationCreateForm();
        this.modalTitle = i18n.global.t("registration.group_action.create");
        this.modalType = "create-registration";
        this.currentGroupCryptedId = groupCryptedId;
      } finally {
        this.groupLoading = false;
      }
    },

    submitModal() {
      if (this.modalType === "create-registration") {
        this.submitRegistrationCreateModal();
      }
    },

    searchModal(params) {
      if (this.modalType === "search-registration") {
        this.groupLoading = true;
        this.registrationListParams["offset"] = 0;
        this.registrationListParams["search"] = params;
        this.getRegistrationList();
        this.registrationListIsFiltered = true;
      }
    },

    submitRegistrationCreateModal() {
      this.groupLoading = true;
      const result = formManager.processForm(this.modalModels);
      const data = { data: result, registrationActorType: null };
      registrationManager.postRegistrationCreateForm(this.currentGroupCryptedId, data);
    },

    refresh(params) {
      this.registrationListParams = dataTableManager.getListParams(
        null,
        params.params.limit,
        params.params.sortOrder,
        params.params.sortByField,
        params.params.keywords,
        params.params.offset,
      );
      this.getRegistrationList();
    },

    async openSearch() {
      this.groupLoading = true;
      this.resetModalContent();
      this.searchStorageKey = "studea-training-registration-" + this.$route.params.trainingId;
      let params;
      if (sessionStorage.getItem(this.searchStorageKey)) {
        params = dataTableManager.getParamsFormSessionStorage(sessionStorage.getItem(this.searchStorageKey));
      }
      try {
        this.modalModels = await trainingManager.getUfaRegistrationSearchForm(this.$route.params.trainingId, params);
        this.modalType = "search-registration";
        this.modalTitle = i18n.global.tc("global.search.title", 2, {
          name: i18n.global.t("studea_manager.registration.list.title").toLowerCase(),
        });
      } finally {
        this.groupLoading = false;
      }
    },

    async resetSearch() {
      this.registrationListParams = dataTableManager.getListParams();
      await this.getRegistrationList();
      this.registrationListIsFiltered = false;
      sessionStorage.removeItem("studea-training-registration-" + this.$route.params.trainingId);
    },

    async deleteGroup(cryptedId) {
      const alert = await notificationManager.showAlert(
        "question",
        this.$t("registration.group_action.delete.confirmation"),
        "",
        true,
        true,
      );
      if (!alert.isConfirmed) return;
      this.groupLoading = true;
      const response = await trainingManager.delete("group", cryptedId);
      if (!response.success) {
        notificationManager.showNotification("error", response.message);
      } else {
        notificationManager.showNotification("success", this.$t("registration.group_action.delete.success"));
        await this.getTraining();
      }
    },

    ...mapActions(useMainStore, ["setAppLoading", "closeSideBar"]),
    ...mapActions(useStudeaManagerStore, ["setTrainingName"]),
  },
};
</script>

<style lang="scss" scoped>
.settings {
  width: 35px;
  height: 30px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  position: relative;

  &:hover {
    font-size: 20px;
    color: $green;
  }
}

.settings + .settings {
  &:before {
    content: "";
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    height: 12px;
    width: 1px;
    background: var(--primary-color);

    @include up-to-md {
      height: 0;
      width: 0;
    }
  }
}

.collapse-content {
  padding: 20px;
}
</style>
